import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { setFiltersVisibility } from '../../../../../common/actions';
import FilterModal from '../../../../../common/components/FilterModal';
import { SearchableSelect, Dropdown } from '../../../../../common/ui';
import Axios from '../../../../../common/util/Axios';
import styles from './styles.module.css';

export const STATUS_OPTIONS = [
  { value: 'BALANCED', label: 'Balanced' },
  { value: 'SHORT', label: 'Short' },
  { value: 'OVER', label: 'Over' },
  { value: '-', label: '-' },
];

const CashBalanceFilterComp = ({
  common: { isFiltersOpen },
  setFiltersVisibility,
  filters,
  setFilters,
  branchId,
  companyId,
  showUserFilter = true,
  showCashierFilter = true,
  showStatusFilter = true,
  showPaymentMethodFilter = false,
  paymentLedgerMethods = [],
  isGetPaymentLedgerMethodsLoading = false,
}) => {
  const [cashiers, setCashiers] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedCashier, setSelectedCashier] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');

  const cashiersWithLabel = useMemo(
    () =>
      cashiers.map((c) => ({
        ...c,
        displayLabel: c.name
          ? `${c.name}${c.cashierNo ? ` (${c.cashierNo})` : ''}`
          : c.cashierNo || String(c.id),
      })),
    [cashiers]
  );

  const usersWithLabel = useMemo(() => {
    const list = users.map((u) => ({ ...u, displayLabel: u.name || u.id || '' }));
    if (selectedUser && !list.some((u) => String(u.id) === String(selectedUser.id))) {
      return [
        { ...selectedUser, displayLabel: selectedUser.name || selectedUser.id || '' },
        ...list,
      ];
    }
    return list;
  }, [users, selectedUser]);

  const paymentMethodOptions = useMemo(
    () =>
      (paymentLedgerMethods || []).map((method) => ({
        value: method.id,
        label: method.name || method.typeName || String(method.id),
      })),
    [paymentLedgerMethods]
  );

  useEffect(() => {
    if (!isFiltersOpen || !branchId || !showCashierFilter) return;
    const loadCashiers = async () => {
      try {
        const res = await Axios.get('/api/dashboard/cashiers/find-all-by', {
          params: { branchId },
        });
        setCashiers((res.data && res.data.data) || []);
      } catch (err) {
        console.error('Failed to load cashiers', err);
      }
    };
    loadCashiers();
  }, [isFiltersOpen, branchId, showCashierFilter]);

  const searchUsers = useCallback(
    async (keyword) => {
      if (!companyId) return;
      setUsersLoading(true);
      try {
        const params = { companyId, limit: 20, page: 1 };
        if (keyword && keyword.trim()) {
          params.name = { like: `%${keyword.trim()}%` };
        }
        const res = await Axios.get('/api/dashboard/users/find-all', { params });
        setUsers((res.data && res.data.data) || []);
      } catch (err) {
        console.error('Failed to search users', err);
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (!isFiltersOpen) return;
    setSelectedCashier(filters.cashier || null);
    setSelectedUser(filters.user || null);
    setSelectedStatus(filters.status || '');
    setSelectedPaymentMethodId(filters.paymentMethod?.id || '');
  }, [
    isFiltersOpen,
    filters.cashier,
    filters.user,
    filters.status,
    filters.paymentMethod,
  ]);

  const handleApply = () => {
    const newFilters = { ...filters };

    if (showCashierFilter) {
      if (selectedCashier) newFilters.cashier = selectedCashier;
      else delete newFilters.cashier;
    }
    if (showUserFilter) {
      if (selectedUser) newFilters.user = selectedUser;
      else delete newFilters.user;
    }
    if (showStatusFilter) {
      if (selectedStatus) newFilters.status = selectedStatus;
      else delete newFilters.status;
    }
    if (showPaymentMethodFilter) {
      const selectedMethod = (paymentLedgerMethods || []).find(
        (method) => String(method.id) === String(selectedPaymentMethodId)
      );
      if (selectedMethod) {
        newFilters.paymentMethod = {
          id: selectedMethod.id,
          name: selectedMethod.name,
          typeName: selectedMethod.typeName,
        };
      } else {
        delete newFilters.paymentMethod;
      }
    }

    setFilters(newFilters);
    setFiltersVisibility(false);
  };

  return (
    <FilterModal title="Filter by" onApply={handleApply}>
      {showPaymentMethodFilter && (
        <>
          <div className={styles.filterOptionName}>Payment Method</div>
          <Dropdown
            options={paymentMethodOptions}
            value={selectedPaymentMethodId}
            onChange={(val) => setSelectedPaymentMethodId(val || '')}
            placeholder={
              isGetPaymentLedgerMethodsLoading
                ? 'Loading payment methods'
                : 'Select payment method'
            }
            disabled={isGetPaymentLedgerMethodsLoading}
          />
        </>
      )}
      {showCashierFilter && (
        <>
          <div className={styles.filterOptionName}>Cashier Point</div>
          <SearchableSelect
            options={cashiersWithLabel}
            getOptionLabel={(o) => o.displayLabel}
            getOptionValue={(o) => o.id}
            value={selectedCashier?.id}
            onChange={(id) => {
              if (!id) setSelectedCashier(null);
            }}
            onSelect={(opt) => setSelectedCashier(opt)}
            placeholder="Select cashier point"
          />
        </>
      )}
      {showUserFilter && (
        <>
          {showCashierFilter && <div className={styles.spacer} />}
          <div className={styles.filterOptionName}>User</div>
          <SearchableSelect
            async
            options={usersWithLabel}
            getOptionLabel={(o) => o.displayLabel}
            getOptionValue={(o) => o.id}
            value={selectedUser?.id}
            onChange={(id) => {
              if (!id) setSelectedUser(null);
            }}
            onSelect={(opt) => setSelectedUser(opt)}
            onSearch={searchUsers}
            isLoading={usersLoading}
            placeholder="Search by name"
            minSearchLength={0}
            searchDebounceMs={400}
          />
        </>
      )}
      {showStatusFilter && (
        <>
          <div className={styles.spacer} />
          <div className={styles.filterOptionName}>Status</div>
          <Dropdown
            options={STATUS_OPTIONS}
            value={selectedStatus}
            onChange={(val) => setSelectedStatus(val || '')}
            placeholder="Select status"
          />
        </>
      )}
    </FilterModal>
  );
};

CashBalanceFilterComp.propTypes = {
  common: PropTypes.shape({ isFiltersOpen: PropTypes.bool }).isRequired,
  setFiltersVisibility: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showUserFilter: PropTypes.bool,
  showCashierFilter: PropTypes.bool,
  showStatusFilter: PropTypes.bool,
  showPaymentMethodFilter: PropTypes.bool,
  paymentLedgerMethods: PropTypes.array,
  isGetPaymentLedgerMethodsLoading: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  common: state.commonState,
  branchId: state.authState.branchId,
  companyId: state.authState.companyId,
  paymentLedgerMethods: state.cashBalanceState.paymentLedgerMethods,
  isGetPaymentLedgerMethodsLoading:
    state.cashBalanceState.isGetPaymentLedgerMethodsLoading,
});

export default connect(mapStateToProps, {
  setFiltersVisibility,
})(CashBalanceFilterComp);
